import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, List } from 'lucide-react';
import { getClaimSummary, listManufacturers } from '../../services/pocApi';
import useApiResource from '../../components/poc/useApiResource';
import ClaimTable from '../../components/poc/ClaimTable';
import {
    Disclaimer, EmptyState, ErrorNotice, LoadingState, PageHeader, Panel, POC_LINKS, SubNav,
} from '../../components/poc/PocUi';
import { formatNumber, manufacturerName, modelLabel } from '../../components/poc/pocFormat';

const CARDS = [
    { key: 'total', label: 'Total Claims' },
    { key: 'ACCEPTED', label: 'Accepted' },
    { key: 'REJECTED', label: 'Rejected' },
    { key: 'MANUAL_REVIEW', label: 'Manual Review' },
    { key: 'EVALUATION_ERROR', label: 'Evaluation Errors' },
];

function SummaryCards({ summary }) {
    const value = (key) => (key === 'total' ? summary.total : summary.by_status[key] ?? 0);
    const cards = summary.by_status.PENDING_EVALUATION > 0
        ? [...CARDS, { key: 'PENDING_EVALUATION', label: 'Pending Evaluation' }]
        : CARDS;
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" aria-label="Claim counts">
            {cards.map((card) => {
                const query = card.key === 'total' ? '' : `?status=${card.key}`;
                return (
                    <Link key={card.key} to={`/poc-claims/history${query}`}
                          className="bg-surface border border-line p-5 hover:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">{card.label}</p>
                        <p className="text-3xl font-light mt-3" data-testid={`count-${card.key}`}>{formatNumber(value(card.key))}</p>
                    </Link>
                );
            })}
        </div>
    );
}

function ManufacturerTable({ rows, manufacturers }) {
    if (!rows.length) return <EmptyState>No POC claims have been submitted.</EmptyState>;
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <caption className="sr-only">Claims by manufacturer</caption>
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-text-muted">
                        <th scope="col" className="py-2 pr-4 font-semibold">Manufacturer</th>
                        <th scope="col" className="py-2 pr-4 font-semibold text-right">Total</th>
                        <th scope="col" className="py-2 pr-4 font-semibold text-right">Accepted</th>
                        <th scope="col" className="py-2 pr-4 font-semibold text-right">Rejected</th>
                        <th scope="col" className="py-2 pr-4 font-semibold text-right">Manual Review</th>
                        <th scope="col" className="py-2 font-semibold text-right">Errors</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.manufacturer_code || 'none'} className="border-t border-line">
                            <th scope="row" className="py-2 pr-4 text-left font-normal">
                                {row.manufacturer_code ? (
                                    <Link className="underline underline-offset-2" to={`/poc-claims/history?manufacturer=${row.manufacturer_code}`}>
                                        {manufacturerName(manufacturers, row.manufacturer_code)}
                                    </Link>
                                ) : 'Not specified'}
                            </th>
                            <td className="py-2 pr-4 text-right">{row.total}</td>
                            <td className="py-2 pr-4 text-right">{row.by_status?.ACCEPTED ?? 0}</td>
                            <td className="py-2 pr-4 text-right">{row.by_status?.REJECTED ?? 0}</td>
                            <td className="py-2 pr-4 text-right">{row.by_status?.MANUAL_REVIEW ?? 0}</td>
                            <td className="py-2 text-right">{row.by_status?.EVALUATION_ERROR ?? 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p className="text-[11px] text-text-muted mt-3">Only manufacturers with submitted POC claims are listed.</p>
        </div>
    );
}

/** Phase 5 (F-6): counts per catalogue model; claims without a catalogue model are grouped per manufacturer. */
function ModelTable({ rows, manufacturers }) {
    if (!rows.length) return <EmptyState>No POC claims have been submitted.</EmptyState>;
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <caption className="sr-only">Claims by model</caption>
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-text-muted">
                        <th scope="col" className="py-2 pr-4 font-semibold">Manufacturer</th>
                        <th scope="col" className="py-2 pr-4 font-semibold">Model</th>
                        <th scope="col" className="py-2 pr-4 font-semibold text-right">Total</th>
                        <th scope="col" className="py-2 pr-4 font-semibold text-right">Accepted</th>
                        <th scope="col" className="py-2 pr-4 font-semibold text-right">Rejected</th>
                        <th scope="col" className="py-2 pr-4 font-semibold text-right">Manual Review</th>
                        <th scope="col" className="py-2 font-semibold text-right">Errors</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={`${row.manufacturer_code || 'none'}:${row.model_code || 'none'}`} className="border-t border-line">
                            <td className="py-2 pr-4">{row.manufacturer_code ? manufacturerName(manufacturers, row.manufacturer_code) : 'Not specified'}</td>
                            <th scope="row" className="py-2 pr-4 text-left font-normal">
                                {row.model_code && row.manufacturer_code ? (
                                    <Link className="underline underline-offset-2"
                                          to={`/poc-claims/history?manufacturer=${row.manufacturer_code}&model=${encodeURIComponent(row.model_code)}`}>
                                        {modelLabel(row)}
                                    </Link>
                                ) : 'No catalogue model'}
                            </th>
                            <td className="py-2 pr-4 text-right">{row.total}</td>
                            <td className="py-2 pr-4 text-right">{row.by_status?.ACCEPTED ?? 0}</td>
                            <td className="py-2 pr-4 text-right">{row.by_status?.REJECTED ?? 0}</td>
                            <td className="py-2 pr-4 text-right">{row.by_status?.MANUAL_REVIEW ?? 0}</td>
                            <td className="py-2 text-right">{row.by_status?.EVALUATION_ERROR ?? 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p className="text-[11px] text-text-muted mt-3">
                Only models with submitted POC claims are listed. "No catalogue model" groups manufacturer-level claims and model codes that are not in the catalogue.
            </p>
        </div>
    );
}

export default function PocClaimsDashboard() {
    const summary = useApiResource((signal) => getClaimSummary(signal), []);
    // Names are a convenience: if the catalogue cannot be read, codes are shown instead.
    const manufacturers = useApiResource((signal) => listManufacturers(signal), []);
    const names = manufacturers.data || [];
    const refresh = () => {
        summary.reload();
        manufacturers.reload();
    };

    return (
        <div className="space-y-8 max-w-7xl mx-auto">
            <SubNav label="POC claim management" links={POC_LINKS} />
            <PageHeader
                title="POC Claim Dashboard"
                subtitle="Warranty claims evaluated by the deterministic ICMS policy engine against the active warranty policies in the database."
                actions={<>
                    <Link to="/poc-claims/new" className="btn btn-primary gap-2"><Plus className="w-4 h-4" aria-hidden="true" />Submit New Claim</Link>
                    <Link to="/poc-claims/history" className="btn btn-secondary gap-2"><List className="w-4 h-4" aria-hidden="true" />View All Claims</Link>
                    <button type="button" className="btn btn-secondary gap-2" onClick={refresh} disabled={summary.loading}>
                        <RefreshCw className="w-4 h-4" aria-hidden="true" />Refresh Dashboard
                    </button>
                </>}
            />
            <Disclaimer />

            {summary.loading && !summary.data && <LoadingState label="Loading claim summary…" />}
            <ErrorNotice error={summary.error} onRetry={summary.reload} />

            {summary.data && (
                <div className={`space-y-8 ${summary.loading ? 'opacity-60' : ''}`} aria-busy={summary.loading}>
                    <SummaryCards summary={summary.data} />
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
                        <Panel title="Claims by Manufacturer" className="xl:col-span-5">
                            <ManufacturerTable rows={summary.data.by_manufacturer} manufacturers={names} />
                        </Panel>
                        <Panel title="Recent Claims" className="xl:col-span-7"
                               actions={<Link to="/poc-claims/history" className="text-[11px] font-semibold uppercase tracking-widest underline underline-offset-2">View all</Link>}>
                            {summary.data.recent.length
                                ? <ClaimTable items={summary.data.recent} manufacturers={names} caption="Most recent POC claims" />
                                : <EmptyState>No POC claims have been submitted.</EmptyState>}
                        </Panel>
                    </div>
                    <Panel title="Claims by Model">
                        <ModelTable rows={summary.data.by_model || []} manufacturers={names} />
                    </Panel>
                </div>
            )}
        </div>
    );
}
